import { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiTag } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { getCategories, createAdminCategory, updateAdminCategory, deleteAdminCategory } from '../../services/api';
import AdminLayout from '../../components/layout/AdminLayout';
import './AdminCategories.css';

const EMPTY_FORM = { name: '', description: '', icon: '', order: 0, isActive: true };

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [errors, setErrors] = useState({});

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCategories({ limit: 100, sort: 'order' });
      setCategories(data.categories || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview('');
    setErrors({});
    setEditing(null);
    setModal('add');
  };

  const openEdit = (cat) => {
    setForm({
      name: cat.name || '',
      description: cat.description || '',
      icon: cat.icon || '',
      order: cat.order ?? 0,
      isActive: cat.isActive !== false,
    });
    setImageFile(null);
    setImagePreview(cat.image ? `http://localhost:5000${cat.image}` : '');
    setErrors({});
    setEditing(cat);
    setModal('edit');
  };

  const closeModal = () => { setModal(null); setEditing(null); };

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: val }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: '' }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Category name is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('description', form.description);
      fd.append('icon', form.icon);
      fd.append('order', form.order);
      fd.append('isActive', form.isActive);
      if (imageFile) fd.append('image', imageFile);

      if (modal === 'edit' && editing) {
        const { data } = await updateAdminCategory(editing._id, fd);
        setCategories((prev) => prev.map((c) => c._id === editing._id ? data.category : c));
        toast.success('Category updated');
      } else {
        const { data } = await createAdminCategory(fd);
        setCategories((prev) => [...prev, data.category]);
        toast.success('Category created');
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? This cannot be undone.`)) return;
    setDeletingId(cat._id);
    try {
      await deleteAdminCategory(cat._id);
      setCategories((prev) => prev.filter((c) => c._id !== cat._id));
      toast.success('Category deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <AdminLayout
      title="Categories"
      action={
        <button className="btn btn-primary btn-sm" onClick={openAdd}>
          <FiPlus size={15} /> Add Category
        </button>
      }
    >
      <div className="acat-page">
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><FiTag size={40} /></div>
            <h3>No categories yet</h3>
            <p>Add your first product category to get started.</p>
            <button className="btn btn-primary" onClick={openAdd}><FiPlus size={15} /> Add Category</button>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 52 }}>#</th>
                  <th>Category</th>
                  <th>Slug</th>
                  <th style={{ textAlign: 'center' }}>Order</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat._id} style={{ opacity: deletingId === cat._id ? 0.5 : 1 }}>
                    <td>
                      {cat.image ? (
                        <img src={`http://localhost:5000${cat.image}`} alt={cat.name} className="acat-thumb" />
                      ) : (
                        <span className="acat-icon-placeholder">{cat.icon || '🏷️'}</span>
                      )}
                    </td>
                    <td>
                      <p className="font-semi">{cat.name}</p>
                      {cat.description && <p className="text-muted text-sm">{cat.description}</p>}
                    </td>
                    <td className="text-muted text-sm">{cat.slug}</td>
                    <td style={{ textAlign: 'center' }}>{cat.order ?? 0}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`badge ${cat.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="acat-actions">
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(cat)}>
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          className="btn btn-ghost btn-sm acat-del-btn"
                          onClick={() => handleDelete(cat)}
                          disabled={deletingId === cat._id}
                        >
                          {deletingId === cat._id ? <span className="spinner spinner-sm" /> : <FiTrash2 size={14} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal ──────────────────────────────────────────────────────────── */}
      {modal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-box">
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'add' ? 'Add Category' : 'Edit Category'}</h3>
              <button className="modal-close" onClick={closeModal}><FiX size={18} /></button>
            </div>

            <div className="modal-body">
              {/* Image uploader */}
              <div className="acat-image-upload" onClick={() => document.getElementById('catImg').click()}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="acat-img-preview" />
                ) : (
                  <div className="acat-img-placeholder">
                    <span style={{ fontSize: '2rem' }}>{form.icon || '📷'}</span>
                    <span className="text-sm text-muted">Click to upload image</span>
                  </div>
                )}
                <input id="catImg" type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
              </div>

              <div className="acat-form-grid">
                <div className="form-group">
                  <label className="form-label">Category Name *</label>
                  <input
                    className={`form-input${errors.name ? ' error' : ''}`}
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Electronics"
                    autoFocus
                  />
                  {errors.name && <span className="form-error">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">Icon (emoji)</label>
                  <input
                    className="form-input"
                    value={form.icon}
                    onChange={set('icon')}
                    placeholder="💻"
                  />
                  <span className="form-hint">Optional emoji shown when no image is set</span>
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    rows={2}
                    value={form.description}
                    onChange={set('description')}
                    placeholder="Short description of this category..."
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Display Order</label>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    value={form.order}
                    onChange={set('order')}
                  />
                  <span className="form-hint">Lower numbers appear first</span>
                </div>

                <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                  <label className="acat-status-toggle">
                    <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
                    <span className="acat-toggle-slider" />
                    <span>Active</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <><span className="spinner spinner-sm" /> Saving…</> : <><FiSave size={15} /> Save Category</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
